import { data } from 'react-router';

import { apiIsomorphicFetchInvoice } from '#/api/invoice/apiFetchInvoice';
import { RouteModule } from '#/horizon/components/RouteModule';
import { useI18n } from '#/i18n/hooks/useI18n';
import { NotFoundPageLoadable } from '#/pages/NotFound/NotFoundPageLoadable';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { viewInvoiceRoute } from '#/routing/routes';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';

import { ViewInvoicePageLoadable } from './ViewInvoicePageLoadable';

export async function loader({
  request,
  context,
  params: { invoiceId },
}: HorizonAppRouteLoaderArgs<typeof viewInvoiceRoute>) {
  const [{ org, invoice }] = await Promise.all([
    apiIsomorphicFetchInvoice({ request, context })(invoiceId),
    ViewInvoicePageLoadable.load(),
  ]);

  const isDonation =
    invoice.lineItems.length === 1 &&
    invoice.lineItems[0].description === 'Donation';

  if (isDonation) return data(null, { status: 401 });

  return { org, invoice };
}

export default function ViewInvoiceHorizonRoute({
  loaderData,
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n } = useI18n();

  if (!loaderData) return <NotFoundPageLoadable noLayout />;

  return (
    <RouteModule
      title={
        /* TRANSLATORS: invoice page title, in org dashboard */
        i18n('%(invoiceType)s #%(invoiceNumber)s', {
          invoiceNumber: loaderData.invoice.number ?? '',
          invoiceType: i18n('Invoice'),
        })
      }
      robotsNoIndex
    >
      <ViewInvoicePageLoadable
        org={loaderData.org}
        invoice={loaderData.invoice}
      />
    </RouteModule>
  );
}
